import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Flex, Switch, Spin, message, Typography } from 'antd'
import { CarrierCapability } from '../helpers/types/inventory'
import {
	fetchCarrierCapability,
	upsertCarrierCapability
} from '../api/inventory'

const { Text } = Typography

interface CapabilityRow {
	key: keyof Omit<CarrierCapability, 'carrierId'>
	label: string
	emoji: string
}

const CAPABILITY_ROWS: CapabilityRow[] = [
	{ key: 'has_hvac', label: 'HVAC / Climate', emoji: '❄️' },
	{ key: 'has_heating', label: 'Heating', emoji: '🔥' },
	{ key: 'has_video', label: 'Video Surveillance', emoji: '📹' },
	{ key: 'has_fire_safety', label: 'Fire Safety', emoji: '🧯' }
]

const DEFAULT_CAP: Omit<CarrierCapability, 'carrierId'> = {
	has_hvac: false,
	has_heating: false,
	has_video: false,
	has_fire_safety: false
}

export const CarrierOneCapabilities: FC<{ carrierId: number }> = ({
	carrierId
}) => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState<string | null>(null)
	const [capability, setCapability] =
		useState<Omit<CarrierCapability, 'carrierId'>>(DEFAULT_CAP)

	useEffect(() => {
		setLoading(true)
		fetchCarrierCapability(carrierId)
			.then((data) => {
				setCapability({
					has_hvac: data.has_hvac,
					has_heating: data.has_heating,
					has_video: data.has_video,
					has_fire_safety: data.has_fire_safety
				})
			})
			.catch(() => {
				// 404 means no record yet — default all false
				setCapability(DEFAULT_CAP)
			})
			.finally(() => setLoading(false))
	}, [carrierId])

	const handleToggle = async (
		key: keyof Omit<CarrierCapability, 'carrierId'>,
		value: boolean
	) => {
		const next = { ...capability, [key]: value }
		setCapability(next)
		setSaving(key)
		try {
			await upsertCarrierCapability(carrierId, next)
		} catch {
			message.error(t('Error'))
			setCapability(capability)
		} finally {
			setSaving(null)
		}
	}

	if (loading) {
		return (
			<Flex justify="center" style={{ paddingTop: 40 }}>
				<Spin />
			</Flex>
		)
	}

	return (
		<Flex vertical gap={0} style={{ maxWidth: 480 }}>
			{CAPABILITY_ROWS.map(({ key, label, emoji }) => (
				<Flex
					key={key}
					align="center"
					justify="space-between"
					style={{
						padding: '14px 20px',
						borderBottom: '1px solid #E8EEF4',
						backgroundColor: '#fff',
						borderRadius:
							key === 'has_hvac'
								? '12px 12px 0 0'
								: key === 'has_fire_safety'
									? '0 0 12px 12px'
									: undefined
					}}>
					<Flex align="center" gap={12}>
						<span style={{ fontSize: 22 }}>{emoji}</span>
						<Text
							style={{ fontSize: 15, fontWeight: 500 }}>
							{label}
						</Text>
					</Flex>
					<Switch
						checked={capability[key]}
						loading={saving === key}
						onChange={(v) => handleToggle(key, v)}
						style={{
							backgroundColor: capability[key]
								? '#3894EB'
								: undefined
						}}
					/>
				</Flex>
			))}
		</Flex>
	)
}
